import { Head, useForm } from '@inertiajs/react'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { ArrowLeft } from "lucide-react"
import { Link } from '@inertiajs/react'
import CountrySearch from '@/components/country-search'
import { FlashMessage } from '@/components/flash-message'
import { InputField, TextareaField, SelectField } from '@/components/form-field'

interface Buyer {
  id: number
  name: string
  salesforce_id: string
  code: string
  region: string
  country: string
  state: string
  city: string
  postal_code: string
  sector: string
  notes: string
}

interface Props {
  buyer: Buyer
  errors?: Record<string, string>
}

const sectorOptions = [
  { value: 'commercial', label: 'Commercial' },
  { value: 'intra_company', label: 'Intra-Company' },
  { value: 'public_sector', label: 'Public Sector' }
]

export default function Edit({ buyer, errors: initialErrors }: Props) {
  const { data, setData, put, processing, errors } = useForm({
    name: buyer.name || '',
    salesforce_id: buyer.salesforce_id || '',
    code: buyer.code || '',
    region: buyer.region || '',
    country: buyer.country || '',
    state: buyer.state || '',
    city: buyer.city || '',
    postal_code: buyer.postal_code || '',
    sector: buyer.sector || '',
    notes: buyer.notes || '',
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    put(`/buyers/${buyer.id}`, {
      preserveScroll: true,
      onSuccess: () => {
        // Handle success if needed
      },
      onError: () => {
        // Handle error if needed
      }
    })
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setData(name as keyof typeof data, value)
  }

  const handleSelectChange = (name: string, value: string) => {
    setData(name as keyof typeof data, value)
  }


  return (
    <>
      <Head title="Edit Buyer" />
      <div className="container mx-auto py-6">
        <div className="flex items-center gap-4 mb-6">
          <Link href="/buyers">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <h1 className="text-3xl font-bold">Edit Buyer</h1>
        </div>

        <FlashMessage />

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Information Card */}
            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <InputField
                    name="name"
                    label="Name"
                    data={data}
                    errors={errors}
                    onChange={handleInputChange}
                    readOnly={true}
                    required={true}
                  />

                  <InputField
                    name="salesforce_id"
                    label="Salesforce ID"
                    data={data}
                    errors={errors}
                    onChange={handleInputChange}
                    readOnly={true}
                    required={true}
                  />

                  <InputField
                    name="code"
                    label="Code"
                    data={data}
                    errors={errors}
                    onChange={handleInputChange}
                    readOnly={!!buyer.code}
                  />

                  <SelectField
                    name="sector"
                    label="Sector"
                    data={data}
                    errors={errors}
                    options={sectorOptions}
                    onValueChange={handleSelectChange}
                  />

                  <TextareaField
                    name="notes"
                    label="Notes"
                    data={data}
                    errors={errors}
                    onChange={handleInputChange}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Location Information Card */}
            <Card>
              <CardHeader>
                <CardTitle>Location Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <InputField
                    name="region"
                    label="Region"
                    data={data}
                    errors={errors}
                    onChange={handleInputChange}
                  />

                  <div className="space-y-2">
                    <label htmlFor="country">Country</label>
                    <CountrySearch
                      value={data.country}
                      onChange={(value: string) => handleSelectChange('country', value)}
                    />
                    {errors?.country && <p className="text-sm text-red-500">{errors.country}</p>}
                  </div>

                  <InputField
                    name="city"
                    label="City"
                    data={data}
                    errors={errors}
                    onChange={handleInputChange}
                  />

                  <InputField
                    name="state"
                    label="State"
                    data={data}
                    errors={errors}
                    onChange={handleInputChange}
                  />

                  <InputField
                    name="postal_code"
                    label="Postal Code"
                    data={data}
                    errors={errors}
                    onChange={handleInputChange}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="flex justify-end gap-4 mt-6">
            <Link href="/buyers">
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </Link>
            <Button type="submit" disabled={processing}>
              Update Buyer
            </Button>
          </div>
        </form>
      </div>
    </>
  )
}
